import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Plus, Trash2, Pencil, Package, X, Save, Users } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface ExtrasType {
    id: string;
    label: string;
    category_id: string | null;
    scope_planning: boolean | number;
    scope_evenementiel: boolean | number;
}

interface ExtrasCategory {
    id: string;
    name: string;
}

interface ExtrasPartner {
    id: string;
    name: string;
    types: ExtrasType[];
    categories: ExtrasCategory[];
}

interface ExtrasPerson {
    id: string;
    first_name: string;
    last_name: string;
    phone?: string;
    email?: string;
    extra_type_id: string | null;
}

/** Formulaire compact d'ajout/édition d'une personne enregistrée pour un type d'extra précis (ex: Jean
 *  Dupont, hôtesse) — sélectionnable ensuite à la création d'une demande d'extras de ce type, demande
 *  explicite d'Emeni ("ajouter un type doit être relié aux extras que je rentre dans ce type"). */
function PersonForm({ initial, onSubmit, onCancel }: {
    initial?: { first_name: string; last_name: string; phone?: string; email?: string };
    onSubmit: (v: { first_name: string; last_name: string; phone: string; email: string }) => Promise<void>; onCancel: () => void;
}) {
    const [firstName, setFirstName] = React.useState(initial?.first_name || '');
    const [lastName, setLastName] = React.useState(initial?.last_name || '');
    const [phone, setPhone] = React.useState(initial?.phone || '');
    const [email, setEmail] = React.useState(initial?.email || '');
    const [busy, setBusy] = React.useState(false);

    const submit = async () => {
        if (!firstName.trim() || !lastName.trim()) return;
        setBusy(true);
        try {
            await onSubmit({ first_name: firstName.trim(), last_name: lastName.trim(), phone: phone.trim(), email: email.trim() });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Prénom" autoFocus
                    className="flex-1 min-w-[100px] bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Nom"
                    className="flex-1 min-w-[100px] bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone (optionnel)"
                    className="flex-1 min-w-[100px] bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optionnel)"
                    className="flex-1 min-w-[100px] bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                <Button size="sm" onClick={submit} disabled={busy || !firstName.trim() || !lastName.trim()} className="shrink-0">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                </Button>
                <Button size="sm" variant="secondary" onClick={onCancel} className="shrink-0"><X size={13} /></Button>
            </div>
        </div>
    );
}

const UNCATEGORIZED = 'none';

function TypeForm({ initialLabel = '', onSubmit, onCancel }: {
    initialLabel?: string;
    onSubmit: (label: string) => Promise<void>; onCancel: () => void;
}) {
    const [label, setLabel] = React.useState(initialLabel);
    const [busy, setBusy] = React.useState(false);

    const submit = async () => {
        if (!label.trim()) return;
        setBusy(true);
        try {
            await onSubmit(label.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Agent de sécurité" autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
                className="flex-1 min-w-[140px] bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
            <Button size="sm" onClick={submit} disabled={busy || !label.trim()} className="shrink-0">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            </Button>
            <Button size="sm" variant="secondary" onClick={onCancel} className="shrink-0"><X size={13} /></Button>
        </div>
    );
}

export const ExtrasPartnerCategoryTypesPage: React.FC = () => {
    const { id, categoryId } = useParams<{ id: string; categoryId: string }>();
    const navigate = useNavigate();

    const [partner, setPartner] = React.useState<ExtrasPartner | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    const [editingTypeId, setEditingTypeId] = React.useState<string | null>(null);
    const [renaming, setRenaming] = React.useState(false);
    const [renameValue, setRenameValue] = React.useState('');
    const [savingRename, setSavingRename] = React.useState(false);
    const [error, setError] = React.useState('');

    // Toutes les personnes du partenaire, groupées ensuite par extra_type_id — chaque type affiche
    // uniquement les siennes, avec son propre formulaire d'ajout.
    const [people, setPeople] = React.useState<ExtrasPerson[]>([]);
    const [addingPersonFor, setAddingPersonFor] = React.useState<string | null>(null);
    const [editingPersonId, setEditingPersonId] = React.useState<string | null>(null);

    const loadPeople = React.useCallback(() => {
        if (!id) return;
        moduleApi.getExtrasPartnerPeople(id).then((d: any) => setPeople(Array.isArray(d) ? d : [])).catch(() => setPeople([]));
    }, [id]);

    React.useEffect(() => { loadPeople(); }, [loadPeople]);

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getExtrasPartner(id).then((p: any) => setPartner(p || null)).catch(() => setPartner(null)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!partner || !categoryId) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Partenaire introuvable.</p>
                <Button variant="secondary" onClick={() => navigate('/extras-partenaires')}><ArrowLeft size={15} /> Retour</Button>
            </div>
        );
    }

    const isUncategorized = categoryId === UNCATEGORIZED;
    const category = isUncategorized ? { id: UNCATEGORIZED, name: 'Sans catégorie' } : partner.categories.find((c) => c.id === categoryId);
    if (!category) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Catégorie introuvable.</p>
                <Button variant="secondary" onClick={() => navigate(`/extras-partenaires/${id}/types`)}><ArrowLeft size={15} /> Retour au catalogue</Button>
            </div>
        );
    }

    const types = partner.types.filter((t) => isUncategorized ? !t.category_id : t.category_id === categoryId);
    const qLower = q.trim().toLowerCase();
    const filteredTypes = types.filter((t) => !qLower || t.label.toLowerCase().includes(qLower));

    const handleAddType = async (label: string) => {
        if (!id) return;
        try {
            await moduleApi.createExtrasType(id, { label, category_id: isUncategorized ? null : categoryId });
            setAdding(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible d'ajouter ce type.");
        }
    };

    const handleUpdateType = async (typeId: string, label: string) => {
        if (!id) return;
        try {
            await moduleApi.updateExtrasType(id, typeId, { label, category_id: isUncategorized ? null : categoryId });
            setEditingTypeId(null);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible de modifier ce type.");
        }
    };

    // Portée du type — où il devient sélectionnable : bouton "Ajouter un extra" du Planning et/ou formulaire
    // de privatisation (Événementiel). Pastilles indépendantes (pas un radio) : les deux peuvent être actives
    // à la fois. Enregistre en direct au clic, sans passer par le formulaire d'édition du libellé.
    const handleToggleScope = async (t: ExtrasType, field: 'scope_planning' | 'scope_evenementiel') => {
        if (!id) return;
        const nextValue = !t[field];
        setPartner((prev) => prev ? { ...prev, types: prev.types.map((x) => x.id === t.id ? { ...x, [field]: nextValue } : x) } : prev);
        try {
            await moduleApi.updateExtrasType(id, t.id, { [field]: nextValue });
        } catch (e: any) {
            setError(e?.message || 'Impossible de modifier la portée.');
            load();
        }
    };

    const handleRemoveType = async (typeId: string) => {
        if (!id) return;
        if (!confirm('Supprimer ce type ? Les personnes enregistrées dessus seront conservées, simplement détachées.')) return;
        try {
            await moduleApi.deleteExtrasType(id, typeId);
            load();
            loadPeople();
        } catch (e: any) {
            setError(e?.message || "Impossible de retirer ce type.");
        }
    };

    const handleRenameCategory = async () => {
        if (!id || isUncategorized || !renameValue.trim()) return;
        setSavingRename(true);
        try {
            await moduleApi.updateExtrasCategory(id, categoryId, { name: renameValue.trim() });
            setRenaming(false);
            load();
        } catch (e: any) {
            setError(e?.message || 'Impossible de renommer cette catégorie.');
        } finally {
            setSavingRename(false);
        }
    };

    const handleDeleteCategory = async () => {
        if (!id || isUncategorized) return;
        if (!confirm(`Supprimer la catégorie "${category.name}" ? Ses types seront conservés, simplement repassés "sans catégorie".`)) return;
        try {
            await moduleApi.deleteExtrasCategory(id, categoryId);
            navigate(`/extras-partenaires/${id}/types`);
        } catch (e: any) {
            setError(e?.message || 'Impossible de supprimer cette catégorie.');
        }
    };

    const handleAddPerson = async (typeId: string, v: { first_name: string; last_name: string; phone: string; email: string }) => {
        if (!id) return;
        try {
            await moduleApi.createExtrasPartnerPerson(id, { ...v, extra_type_id: typeId });
            setAddingPersonFor(null);
            loadPeople();
        } catch (e: any) {
            setError(e?.message || "Impossible d'ajouter cette personne.");
        }
    };

    const handleUpdatePerson = async (personId: string, typeId: string, v: { first_name: string; last_name: string; phone: string; email: string }) => {
        if (!id) return;
        try {
            await moduleApi.updateExtrasPartnerPerson(id, personId, { ...v, extra_type_id: typeId });
            setEditingPersonId(null);
            loadPeople();
        } catch (e: any) {
            setError(e?.message || 'Impossible de modifier cette personne.');
        }
    };

    const handleRemovePerson = async (personId: string) => {
        if (!id) return;
        try {
            await moduleApi.deleteExtrasPartnerPerson(id, personId);
            loadPeople();
        } catch (e: any) {
            setError(e?.message || 'Impossible de retirer cette personne.');
        }
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <button onClick={() => navigate(`/extras-partenaires/${id}/types`)} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium mb-2">
                    <ArrowLeft size={15} /> Catalogue de types d'extras
                </button>
                {renaming ? (
                    <div className="flex items-center gap-1.5 max-w-sm">
                        <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCategory(); if (e.key === 'Escape') setRenaming(false); }}
                            className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-1.5 text-lg font-bold text-content outline-none focus:border-brand-500" />
                        <Button size="sm" onClick={handleRenameCategory} disabled={savingRename || !renameValue.trim()}>
                            {savingRename ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setRenaming(false)}><X size={13} /></Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><Package size={18} /> {category.name}</h1>
                        {!isUncategorized && (
                            <>
                                <button onClick={() => { setRenaming(true); setRenameValue(category.name); }} className="text-content-muted hover:text-content"><Pencil size={14} /></button>
                                <button onClick={handleDeleteCategory} className="text-red-400 hover:text-red-500"><Trash2 size={14} /></button>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un type..." className="pl-8" />
                </div>
                {!adding && (
                    <Button size="sm" onClick={() => setAdding(true)} className="shrink-0">
                        <Plus size={14} /> Ajouter un type
                    </Button>
                )}
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {adding && (
                <div className="bg-surface rounded-xl border border-border px-4 py-3">
                    <TypeForm onSubmit={handleAddType} onCancel={() => setAdding(false)} />
                </div>
            )}

            <div className="space-y-3">
                {filteredTypes.length === 0 ? (
                    <p className="text-sm text-content-muted">Aucun type ne correspond.</p>
                ) : filteredTypes.map((t) => {
                    const typePeople = people.filter((p) => p.extra_type_id === t.id);
                    return (
                        <div key={t.id} className="bg-surface rounded-xl border border-border px-4 py-3 space-y-3">
                            {editingTypeId === t.id ? (
                                <TypeForm
                                    initialLabel={t.label}
                                    onSubmit={(label) => handleUpdateType(t.id, label)}
                                    onCancel={() => setEditingTypeId(null)}
                                />
                            ) : (
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex-1 min-w-0 truncate text-sm font-bold text-content">{t.label}</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button onClick={() => setEditingTypeId(t.id)} className="text-content-muted hover:text-content"><Pencil size={13} /></button>
                                        <button onClick={() => handleRemoveType(t.id)} className="text-red-400 hover:text-red-500"><Trash2 size={13} /></button>
                                    </div>
                                </div>
                            )}

                            {editingTypeId !== t.id && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[9px] font-bold text-content-muted uppercase tracking-widest">Proposé dans :</span>
                                    <button type="button" onClick={() => handleToggleScope(t, 'scope_planning')}
                                        className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${t.scope_planning ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content-muted hover:border-brand-500/40'}`}>
                                        Planning
                                    </button>
                                    <button type="button" onClick={() => handleToggleScope(t, 'scope_evenementiel')}
                                        className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${t.scope_evenementiel ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content-muted hover:border-brand-500/40'}`}>
                                        Événementiel
                                    </button>
                                </div>
                            )}

                            <div className="pl-3 border-l-2 border-border space-y-2">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Users size={12} /> Personnes enregistrées</h3>
                                    {addingPersonFor !== t.id && (
                                        <button onClick={() => setAddingPersonFor(t.id)} className="flex items-center gap-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                            <Plus size={12} /> Ajouter
                                        </button>
                                    )}
                                </div>

                                {addingPersonFor === t.id && (
                                    <PersonForm onSubmit={(v) => handleAddPerson(t.id, v)} onCancel={() => setAddingPersonFor(null)} />
                                )}

                                {typePeople.length === 0 ? (
                                    <p className="text-xs text-content-muted">Aucune personne enregistrée pour ce type.</p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {typePeople.map((p) => (
                                            <div key={p.id} className="bg-surface-soft rounded-lg border border-border px-3 py-2">
                                                {editingPersonId === p.id ? (
                                                    <PersonForm
                                                        initial={{ first_name: p.first_name, last_name: p.last_name, phone: p.phone, email: p.email }}
                                                        onSubmit={(v) => handleUpdatePerson(p.id, t.id, v)}
                                                        onCancel={() => setEditingPersonId(null)}
                                                    />
                                                ) : (
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <p className="text-xs text-content font-semibold truncate">{p.first_name} {p.last_name}</p>
                                                            {(p.phone || p.email) && <p className="text-[10px] text-content-muted truncate">{[p.phone, p.email].filter(Boolean).join(' · ')}</p>}
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <button onClick={() => setEditingPersonId(p.id)} className="text-content-muted hover:text-content"><Pencil size={12} /></button>
                                                            <button onClick={() => handleRemovePerson(p.id)} className="text-red-400 hover:text-red-500"><Trash2 size={12} /></button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
